import { Phone, ChevronRight, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AI_UI_STATES } from '../../constants/aiSearchIntents';
import { normalizePhone, truncatedName } from './placeHelpers';

const EmergencyCTA = ({
  uiState,
  googlePlaces,
  isAiFallback = false,
  poisonHotline = null,
  className = '',
}) => {
  const topPlace = googlePlaces?.[0];
  const erPhone = topPlace?.phone;
  const showErChip =
    uiState === AI_UI_STATES.EMERGENCY && !isAiFallback && !!erPhone;

  const hasPoisonChip = !!(poisonHotline && poisonHotline.phone);

  if (!showErChip && !hasPoisonChip) return null;

  // Mode 1: full emergency panel (ER button is the anchor)
  if (showErChip) {
    return (
      <EmergencyPanel
        place={topPlace}
        phone={erPhone}
        poisonHotline={hasPoisonChip ? poisonHotline : null}
        className={className}
      />
    );
  }

  // Mode 2: poison-only (non-emergency context — quieter pill)
  return (
    <StandalonePoisonPill hotline={poisonHotline} className={className} />
  );
};

// ──────────────────────────────────────────────────────────────────────
// Mode 1 — Emergency panel
// ──────────────────────────────────────────────────────────────────────

const EmergencyPanel = ({ place, phone, poisonHotline, className }) => {
  const { t } = useTranslation();
  const wrapperClass = [
    'rounded-xl border border-red-200 bg-red-50',
    'px-3 py-3 sm:px-4 sm:py-4',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section
      aria-label={t('emergency_actions', 'Emergency actions')}
      className={wrapperClass}
    >
      <div className="flex items-center gap-2 text-red-700">
        <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
        <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider">
          {t('pet_emergency_label', 'Pet Emergency')}
        </span>
      </div>

      <div className="mt-2 sm:mt-3">
        <PrimaryErButton place={place} phone={phone} />
      </div>

      {poisonHotline ? (
        <>
          <div
            className="my-3 border-t border-red-200/70"
            aria-hidden="true"
          />
          <SecondaryPoisonLink hotline={poisonHotline} />
        </>
      ) : null}
    </section>
  );
};

const PrimaryErButton = ({ place, phone }) => {
  const { t } = useTranslation();
  const placeName = place?.name || 'Emergency Vet';
  const displayName = truncatedName(placeName, 48);
  const ariaLabel = `Call ${placeName}`;
  const telHref = `tel:${normalizePhone(phone)}`;

  return (
    <a
      href={telHref}
      className="flex w-full items-center gap-3 bg-red-600 hover:bg-red-700 active:bg-red-700 text-white px-3 sm:px-4 py-3 sm:py-3.5 rounded-lg transition-colors min-h-[56px] focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 focus:ring-offset-red-50"
      aria-label={ariaLabel}
    >
      <span
        className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/15 flex items-center justify-center"
        aria-hidden="true"
      >
        <Phone className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
      </span>

      <span className="flex-1 min-w-0 flex flex-col text-left">
        <span className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider text-white/85 leading-tight">
          {t('tap_to_call', 'Tap to Call')}
        </span>
        <span className="text-sm sm:text-base font-bold truncate leading-tight mt-0.5">
          {displayName}
        </span>
      </span>

      <ChevronRight
        className="flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5 text-white/85"
        aria-hidden="true"
      />
    </a>
  );
};

const SecondaryPoisonLink = ({ hotline }) => {
  const { t } = useTranslation();
  const name = hotline.name || 'Animal Poison Control';
  const phone = hotline.phone;
  const feeNote = hotline.feeNote || null;
  const telHref = `tel:${normalizePhone(phone)}`;
  const ariaLabel = `Call ${name} at ${phone}`;

  return (
    <a
      href={telHref}
      className="group flex items-center gap-2.5 -mx-1 px-1 py-1 rounded-md text-red-800 hover:text-red-900 focus:outline-none focus:ring-2 focus:ring-red-300"
      aria-label={ariaLabel}
    >
      <Phone
        className="flex-shrink-0 w-3.5 h-3.5 text-red-600"
        aria-hidden="true"
      />
      <span className="flex-1 min-w-0 flex flex-col leading-tight text-left">
        <span className="text-[11px] sm:text-xs font-semibold text-red-700">
          {t('pet_poison_hotline', 'Pet Poison Hotline')}
        </span>
        <span className="text-xs sm:text-[13px] font-medium truncate">
          <span className="underline-offset-2 group-hover:underline">
            {phone}
          </span>
          {feeNote ? (
            <span className="font-normal text-red-700/85"> · {feeNote}</span>
          ) : null}
        </span>
      </span>
      <ChevronRight
        className="flex-shrink-0 w-3.5 h-3.5 text-red-500"
        aria-hidden="true"
      />
    </a>
  );
};

const StandalonePoisonPill = ({ hotline, className }) => {
  const { t } = useTranslation();
  const name = hotline.name || 'Animal Poison Control';
  const phone = hotline.phone;
  const feeNote = hotline.feeNote || null;
  const telHref = `tel:${normalizePhone(phone)}`;
  const ariaLabel = `Call ${name} at ${phone}`;

  const wrapperClass = [
    'flex w-full items-center gap-2.5',
    'bg-red-50 hover:bg-red-100 active:bg-red-100',
    'border border-red-200',
    'text-red-800',
    'px-3 py-2 rounded-lg transition-colors min-h-[44px]',
    'focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-1',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <a href={telHref} className={wrapperClass} aria-label={ariaLabel}>
      <span
        className="flex-shrink-0 w-7 h-7 rounded-full bg-red-100 flex items-center justify-center"
        aria-hidden="true"
      >
        <Phone className="w-3.5 h-3.5 text-red-600" />
      </span>

      <span className="flex-1 min-w-0 flex flex-col text-left">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-red-700 leading-tight">
          {t('pet_poison_hotline', 'Pet Poison Hotline')}
        </span>
        <span className="text-xs sm:text-sm font-semibold text-red-900 truncate leading-tight mt-0.5">
          {phone}
          {feeNote ? (
            <span className="font-normal text-red-700"> · {feeNote}</span>
          ) : null}
        </span>
      </span>

      <ChevronRight
        className="flex-shrink-0 w-4 h-4 text-red-500"
        aria-hidden="true"
      />
    </a>
  );
};

export default EmergencyCTA;
